import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { PlanCards } from "@/components/PlanCards";
import { PLANS, usageText } from "@/lib/constants";
import { RedeemCode } from "@/components/app/RedeemCode";
import { StudentOffer } from "@/components/app/StudentOffer";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Pricing() {
  const { user, refresh } = useAuth();
  const [plan, setPlan] = useState(null);
  const [legal, setLegal] = useState(false);
  const [loading, setLoading] = useState(null);

  const checkout = async () => {
    setLoading(plan);
    try {
      const { data } = await api.post("/billing/checkout", { plan, origin_url: window.location.origin, legal_confirmed: legal });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setLoading(null);
    }
  };
  const cancel = async () => {
    try { await api.post("/billing/cancel"); await refresh(); toast.success("Abonnement résilié. Tu es repassé à l'offre Gratuite."); } catch (e) { toast.error(errMsg(e)); }
  };
  const selected = PLANS.find((p) => p.id === plan);

  return (
    <div data-testid="pricing-page">
      <p className="eyebrow">Offres</p>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">Choisis l'offre qui te convient</h1>
      <p className="mt-2 text-sm text-muted-foreground">Offre actuelle : <b className="text-foreground" data-testid="current-plan-name">{user?.plan_name}{user?.free_access ? " (offerte)" : ""}</b> · utilisation ce mois-ci : {usageText(user)}.</p>
      <div className="mt-10">
        <PlanCards currentPlan={user?.plan} loadingPlan={loading} discount={user?.student_verified ? 0.2 : null} onSelect={(id) => { if (user?.free_access) return toast.info("Tu as déjà un accès Pro illimité offert."); setLegal(false); setPlan(id); }} ctaLabel={(p) => (p.id === "free" ? "Inclus" : `Passer à ${p.name}`)} />
      </div>
      <p className="mt-5 text-sm text-muted-foreground">{user?.student_verified ? "Ta réduction étudiante de 20 % sera appliquée automatiquement au paiement." : "Tu as un code promo ? Saisis-le directement sur la page de paiement sécurisée."}</p>
      <StudentOffer />
      {!user?.free_access && <RedeemCode />}
      {user?.plan !== "free" && !user?.free_access && (
        <AlertDialog>
          <AlertDialogTrigger asChild><button data-testid="cancel-subscription-button" className="mt-8 text-sm font-semibold text-muted-foreground underline hover:text-destructive">Résilier mon abonnement</button></AlertDialogTrigger>
          <AlertDialogContent className="rounded-3xl">
            <AlertDialogHeader><AlertDialogTitle>Résilier l'abonnement ?</AlertDialogTitle><AlertDialogDescription>Tu repasseras immédiatement à l'offre Gratuite (60 min / mois). Tes cours sont conservés.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel className="rounded-full">Garder mon offre</AlertDialogCancel><AlertDialogAction data-testid="cancel-subscription-confirm" onClick={cancel} className="rounded-full bg-destructive text-destructive-foreground">Résilier</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      <Dialog open={!!plan && plan !== "free"} onOpenChange={(v) => !v && setPlan(null)}>
        <DialogContent className="rounded-3xl sm:max-w-md" data-testid="checkout-legal-dialog">
          <DialogHeader>
            <DialogTitle>Passer à {selected?.name} — {selected?.price} / mois</DialogTitle>
            <DialogDescription>Paiement sécurisé par Stripe. Résiliable à tout moment.</DialogDescription>
          </DialogHeader>
          <label className="flex cursor-pointer gap-3 rounded-2xl border p-4 text-sm">
            <Checkbox data-testid="checkout-legal-checkbox" checked={legal} onCheckedChange={(v) => setLegal(!!v)} className="mt-0.5" />
            <span>Je suis <b>majeur(e)</b>, ou mon <b>parent / représentant légal</b> autorise cet abonnement et effectue lui-même le paiement.</span>
          </label>
          <button data-testid="checkout-confirm-button" disabled={!legal || !!loading} onClick={checkout} className="bg-brand mt-2 rounded-full px-6 py-3 text-sm font-semibold text-white disabled:opacity-50">
            {loading ? "Redirection vers le paiement…" : "Continuer vers le paiement"}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
